/**
 * Wave 29 Chunk 4 — /team/performance hub.
 *
 * Shows the live leaderboard (metric + window switchers), my-rank strip,
 * earned badges, points balance, and a quick link to announcements.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Trophy, ChevronLeft, Award, Coins, Megaphone, Loader2, ChevronRight,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/ui/sonner";
import LeaderboardCard from "../components/team/LeaderboardCard";
import BadgeChip from "../components/team/BadgeChip";
import { fmtDate } from "../lib/datetime";

// Same formatter LeaderboardCard uses — mirrored here so the my-rank strip
// renders Hours / signed-minutes correctly too.
function fmtMetric(v, fmt, suffix) {
  if (v == null || v === "") return "—";
  if (fmt === "minutes_to_hours") {
    const n = Number(v) || 0;
    const h = Math.floor(n / 60);
    const m = n % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  }
  if (fmt === "minutes_signed") {
    const n = Math.round(Number(v) || 0);
    if (n === 0) return "on time";
    if (n < 0) return `${Math.abs(n)}m early`;
    return `+${n}m late`;
  }
  return `${v}${suffix || ""}`;
}

const METRICS = [
  { key: "jcs_completed",      label: "JCs done" },
  { key: "jc_completeness",    label: "JC completeness" },
  { key: "photos_uploaded",    label: "Photos" },
  { key: "total_minutes",      label: "Hours" },
  { key: "on_site_ratio",      label: "On-site %" },
  { key: "attendance_streak",  label: "Streak" },
  { key: "early_signin_min",   label: "Earliest sign-in" },
  { key: "late_signin_min",    label: "Latest sign-in" },
];

const WINDOWS = [
  { key: "today", label: "Today" },
  { key: "week",  label: "This week" },
  { key: "month", label: "This month" },
  { key: "year",  label: "This year" },
];

export default function TeamPerformance() {
  const { user } = useAuth();
  const isHr = user && ["owner", "admin"].includes(user.role);
  const [metric, setMetric] = useState("jcs_completed");
  const [window, setWindow] = useState("month");
  const [leaderboard, setLeaderboard] = useState(null);
  const [mine, setMine] = useState(null);
  const [badges, setBadges] = useState([]);
  const [rewards, setRewards] = useState(null);
  const [unreadAnn, setUnreadAnn] = useState(0);
  const [loading, setLoading] = useState(true);
  const [evaluating, setEvaluating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: lb }, { data: m }, { data: b }, { data: r }, { data: a }] = await Promise.all([
        api.get("/team/leaderboard", { params: { metric, window, limit: 20 } }),
        api.get("/team/leaderboard/me", { params: { window } }),
        api.get("/team/badges/me"),
        api.get("/team/rewards/me"),
        api.get("/team/announcements"),
      ]);
      setLeaderboard(lb);
      setMine(m);
      setBadges(b.items || []);
      setRewards(r);
      setUnreadAnn(a.unread || 0);
    } catch (e) {
      toast.error("Could not load performance data.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [metric, window]);

  const evaluateBadges = async () => {
    setEvaluating(true);
    try {
      const { data } = await api.post("/team/badges/evaluate");
      toast.success(`Re-evaluated badges for ${data.users_processed} users (${data.awarded} newly awarded).`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Evaluation failed.");
    } finally { setEvaluating(false); }
  };

  if (!user) return null;
  return (
    <div className="space-y-4" data-testid="team-performance-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to={isHr ? "/team" : "/me"} className="hv-btn hv-btn-ghost text-xs">
          <ChevronLeft className="w-3.5 h-3.5" /> Back
        </Link>
        <h1 className="font-display text-xl sm:text-2xl font-bold flex items-center gap-2">
          <Trophy className="w-5 h-5 text-[#1FB6C1]" /> Team performance
        </h1>
        <div className="flex items-center gap-2">
          <Link
            to="/team/announcements"
            className="hv-btn hv-btn-ghost text-xs relative"
            data-testid="perf-announcements-link"
          >
            <Megaphone className="w-3.5 h-3.5" /> Announcements
            {unreadAnn > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-[#1FB6C1] text-[#0a0b0e] text-[10px] font-bold px-1">
                {unreadAnn}
              </span>
            )}
          </Link>
          {isHr && (
            <button
              type="button"
              onClick={evaluateBadges}
              disabled={evaluating}
              className="hv-btn hv-btn-ghost text-xs disabled:opacity-50"
              data-testid="perf-reeval-badges"
            >
              {evaluating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Award className="w-3.5 h-3.5" />}
              Re-evaluate badges
            </button>
          )}
        </div>
      </div>

      {/* My rank strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="my-rank-strip">
        {(mine?.items || []).map((m) => (
          <button
            key={m.metric}
            type="button"
            onClick={() => setMetric(m.metric)}
            className={`hv-card p-3 text-left transition-colors ${
              metric === m.metric ? "border-[#1FB6C1]/60 bg-[#1FB6C1]/[0.05]" : "hover:border-white/20"
            }`}
            data-testid={`my-rank-${m.metric}`}
          >
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#647087]">{m.label}</div>
            <div className="flex items-end justify-between gap-2 mt-1">
              <div className="font-display text-2xl font-bold tabular-nums">{fmtMetric(m.value, m.format_value, m.suffix)}</div>
              {m.rank != null && (
                <div className="text-right">
                  <div className="text-[10px] text-[#a0aabf]">rank</div>
                  <div className="font-display text-base font-bold text-[#1FB6C1]">#{m.rank}</div>
                </div>
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Metric + window switchers */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 flex-wrap">
          {METRICS.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => setMetric(m.key)}
              className={`text-[11px] px-2.5 py-1 rounded-full border ${
                metric === m.key
                  ? "bg-[#1FB6C1] text-[#0a0b0e] border-[#1FB6C1]"
                  : "border-white/15 text-[#a0aabf] hover:bg-white/5"
              }`}
              data-testid={`metric-${m.key}`}
            >
              {m.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 flex-wrap ml-auto">
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              type="button"
              onClick={() => setWindow(w.key)}
              className={`text-[11px] px-2.5 py-1 rounded-full border ${
                window === w.key
                  ? "bg-white/10 text-white border-white/20"
                  : "border-white/10 text-[#647087] hover:bg-white/5"
              }`}
              data-testid={`window-${w.key}`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          {loading && !leaderboard ? (
            <div className="h-64 hv-card animate-pulse" />
          ) : (
            <LeaderboardCard data={leaderboard} myUserId={user.id} dense />
          )}
        </div>
        <div className="space-y-4">
          {/* Badges */}
          <div className="hv-card p-4" data-testid="my-badges-card">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] inline-flex items-center gap-1">
                <Award className="w-3 h-3" /> My badges
              </div>
              <div className="text-xs text-[#a0aabf]">{badges.length}</div>
            </div>
            {badges.length === 0 ? (
              <p className="text-xs text-[#647087]">
                No badges yet. Clock in regularly and finish job cards to earn your first badge.
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {badges.map((b) => (
                  <BadgeChip key={b.id} badge={b.badge} size="sm" />
                ))}
              </div>
            )}
          </div>
          {/* Rewards */}
          <div className="hv-card p-4" data-testid="my-rewards-card">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] inline-flex items-center gap-1">
                <Coins className="w-3 h-3" /> Points balance
              </div>
            </div>
            <div className="font-display text-3xl font-bold tabular-nums">
              {rewards?.balance ?? 0}
            </div>
            {rewards?.ledger?.length > 0 && (
              <ul className="mt-3 space-y-1 text-[11px] max-h-40 overflow-auto">
                {rewards.ledger.slice(0, 8).map((r) => (
                  <li key={r.id} className="flex items-start justify-between gap-2 py-1 border-b border-white/5 last:border-b-0">
                    <div className="min-w-0">
                      <div className={`font-semibold tabular-nums ${r.points >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                        {r.points >= 0 ? "+" : ""}{r.points}
                      </div>
                      <div className="text-[10px] text-[#a0aabf] truncate">{r.reason}</div>
                    </div>
                    <div className="text-[9px] text-[#647087] shrink-0">{fmtDate(r.at)}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {/* HR grant CTA */}
          {isHr && (
            <Link
              to="/team"
              className="hv-card p-4 block hover:border-[#1FB6C1]/40 transition-colors"
              data-testid="perf-hr-grant-cta"
            >
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] inline-flex items-center gap-1">
                <Coins className="w-3 h-3" /> HR shortcut
              </div>
              <div className="text-sm text-white mt-1">Grant points to a teammate</div>
              <div className="text-[11px] text-[#a0aabf] mt-1 flex items-center gap-0.5">
                Pick from the team list <ChevronRight className="w-3 h-3" />
              </div>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
